import { HTMLAttributes, useContext, useState } from "react";
import { I18nContext } from "../../i18n/useI18n";
import AutoCounterUp from "../../components/CounterUp";
import Divider from "../../components/Divider";
import Stepper from "../../components/Stepper";
import InfiniteScrollRedirect from "../../components/Infinite-Roll/InfiniteScrol";
import PrincipalBanner from "../PrincipalBanner/index";
import SimpleCarousel from "../../components/SimpleCarousel";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";

const Home = () => {
  const { t } = useContext(I18nContext);
  const [currentStep, setCurrentStep] = useState(0);

  // Função para alternar o conteúdo com clique nos steps, sem animação ou smooth scroll
  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };
  return (
    <section className="mx-auto max-w-7xl">
      <PrincipalBanner bgHero="https://static.igem.wiki/teams/5058/imgs/banner-principal.svg">
        <div className="w-full max-w-[50%] md:max-w-[500px] h-auto">
          <img
            className="w-full h-full object-contain"
            alt={t("pages.home.banner.title")}
            src="https://static.igem.wiki/teams/5058/imgs/logo-centralizada.svg"
          />
        </div>

        <h1 className="flex flex-1 items-center px-12 py-6 font-manjari text-2xl text-green md:text-5xl">
          {t(`pages.home.banner.title`)}
        </h1>
      </PrincipalBanner>
      <div className="flex md:flex-row flex-col gap-3 md:gap-0 border-grape px-6 py-8 border-t">
        <div className="flex flex-col gap-2 px-2 w-full md:w-4/5">
          <h2 className="font-literata text-3xl text-yellow">
            {t("pages.home.sections.position-1.title")}
          </h2>
          <h1 className="font-bold font-poppins text-5xl text-green text-left lg:text-7xl">
            {t("pages.home.sections.position-1.subtitle")}
          </h1>
          <p className="font-nunito text-center text-xl">
            {t("pages.home.sections.position-1.description")}
          </p>
        </div>
        <div className="flex flex-col items-center w-full text-center">
          <img
            className="w-2/3 object-contain"
            src="https://static.igem.wiki/teams/5058/imgs/planet.gif"
            width={50}
            height={50}
            alt="planet"
          />
        </div>
      </div>

      <Divider />

      <div className="relative flex justify-around items-center px-6 py-8 min-h-[460px] overflow-hidden">
        <img
          alt="background roots"
          className="-top-[13px] right-0 left-0 -z-1 absolute opacity-50 md:opacity-100 mx-auto w-full max-w-[460px] md"
          src="https://static.igem.wiki/teams/5058/imgs/raizes.svg"
        />
        <div className="relative z-1 md:max-w-[280px]">
          <p className="pr-[10%] md:pr-[10%] text-2xl">
            {t("pages.home.sections.position-2.title")}
          </p>
          <p className="pb-4 font-bold text-5xl">
            <AutoCounterUp
              start={0}
              end={10.7}
              duration={5}
              increaseStep={0.1}
              className="text-green"
            />{" "}
            {t("pages.home.sections.position-2.subtitle")}
          </p>
        </div>

        <div className="relative text-right z-0 md:max-w-[240px]">
          <p className="pb-5 font-nunito text-2xl">
            {t("pages.home.sections.position-2.description")}
          </p>
          <p className="font-literata tracking-[7px]">
            {t("pages.home.sections.position-2.source")}
          </p>
        </div>
      </div>

      <Divider />

      <div
        className={twMerge(
          clsx(
            "text-right xl:flex justify-between items-center",
            "md:grid md:grid-cols-5 px-6 py-8 md:px-10 md:py-12 md:pr-0"
          )
        )}
      >
        <p className="md:col-span-3 p-4 font-poppins text-2xl text-center">
          <span className="text-green">
            high temperatures could reduce productivity by up to 40% By 2030
          </span>
          <br />
          <span className="text-orange">
            51% of the main Brazilian rice-producing areas may no longer have
            ideal climatic conditions for cultivation
          </span>
        </p>
        <img
          src="https://static.igem.wiki/teams/5058/imgs/plantinha-home.svg"
          alt="flowers home"
          className="md:col-span-2 m-auto h-full"
        />
      </div>

      <Divider />
      <div className={twMerge(clsx("relative px-6 pt-10 md:pt-[150px] pb-10"))}>
        <p className={twMerge(clsx("lg:max-w-none font-nunito text-xl"))}>
          <span
            className={twMerge(
              clsx(
                "block md:inline-block text-right md:float-right",
                "md:-mt-24 md:pb-4 md:max-w-[60%]"
              )
            )}
          >
            <em
              className={twMerge(
                clsx("font-literata text-2xl text-orange tracking-[10px]")
              )}
            >
              our mission
            </em>
            <br />
            <em className="font-poppins text-4xl text-green">
              So, how can we increase the crop productivity affected by climate
              change?
            </em>
          </span>
          The <span className="font-bold text-green">YOB</span>
          <span className="font-bold text-orange">3</span> <b>project</b> was
          developed to create a biostimulant that enhances nutritional
          efficiency and abiotic stress tolerance, thereby improving plant
          productivity. Our goal is to produce <b>nicotinamide riboside (NR)</b>
          , a NAD+ precursor, component of the vitamin B3 family with
          significant biological value.
        </p>
      </div>

      <Divider />
      <div className="relative px-20 pt-[150px] pb-[80px] overflow-hidden">
        <img
          className={twMerge(
            clsx(
              "-top-7 left-1/2 z-0 absolute min-w-full",
              "min-h-full transform -translate-x-1/2 object-cover"
            )
          )}
          src="https://static.igem.wiki/teams/5058/imgs/dinheiro-home-2.svg"
          alt="gif looking"
        />
        <p className="relative z-1 bg-gradient-to-t from-primary to-transparent mx-auto px-5 rounded-xl max-w-2xl text-2xl text-center text-secondary">
          current NR synthesis methods involve chemical synthesis, which has
          several disadvantages, such as{" "}
          <span className="font-bold text-yellow">
            complexity and high cost
          </span>
        </p>
      </div>

      <Divider />

      <div className="flex flex-row justify-around p-16 overflow-hidden">
        <img
          className="w-2/3 md:w-1/4"
          src="https://static.igem.wiki/teams/5058/imgs/gif-yob3-comendo.gif"
          alt="gif looking"
        />
        <div className="text-right w-1/2 font-poppins text-4xl text-green">
          we will utilize yeast S. cerevisiae, as a factory due to its
          versatility in genomic manipulation, and fermentation capabilities.
        </div>
      </div>

      <Divider />

      <div>
        <div className="px-12 py-8 w-full md:w-2/3 xl:w-1/2 font-poppins text-3xl text-justify text-orange md:text-left">
          Our team designed a genetic circuit to improve NR production without
          affecting negativelyin biomass growth. To do so, we divided our
          circuit into:
        </div>
        <Stepper
          classNames=" mx-auto"
          steps={[
            {
              step: "1",
              icon: (props: HTMLAttributes<HTMLImageElement>) => (
                <img
                  {...props}
                  className={twMerge(clsx("rounded-full"))}
                  alt="step yeast icon"
                  src="https://static.igem.wiki/teams/5058/imgs/yeast.svg"
                />
              ),
            },
            {
              step: "2",
              icon: (props: HTMLAttributes<HTMLImageElement>) => (
                <img
                  {...props}
                  alt="step bioreactor icon"
                  src="https://static.igem.wiki/teams/5058/imgs/bioreactor.svg"
                />
              ),
            },
            {
              step: "3",
              icon: (props: HTMLAttributes<HTMLImageElement>) => (
                <img
                  {...props}
                  alt="step plant icon"
                  src="https://static.igem.wiki/teams/5058/imgs/plant.svg"
                />
              ),
            },
          ]}
          activeStep={currentStep}
          setActiveStep={(step) => handleStepChange(step)}
        />
        <div className="relative">
          <SimpleCarousel
            contentFull
            currentItem={currentStep}
            onMove={(step: number) => {
              setCurrentStep(step);
            }}
            list={[
              {
                itemCustomClass: clsx({
                  active: currentStep === 0,
                }),
                content: (
                  <p>
                    NR enhance - To increase the NR production, the PHO pathway
                    is stimulated throught a degron system responsive to auxin
                  </p>
                ),
              },
              {
                itemCustomClass: clsx({
                  active: currentStep === 1,
                }),
                content: (
                  <p>
                    NR extraction - The yield extraction will be improved by the
                    flocculation of yeast after attaining a stationary phase of
                    growth;
                  </p>
                ),
              },
              {
                itemCustomClass: clsx({
                  active: currentStep === 2,
                }),
                content: (
                  <p>
                    With the combination of these techniques, we will keep the
                    production affordable for small to large producers and help
                    to mitigate the demand for hazardous chemicals in
                    agriculture.
                  </p>
                ),
              },
            ]}
          ></SimpleCarousel>
          <div className="top-1/2 right-5 left-5 absolute flex justify-between transform -translate-y-1/2">
            <button
              className={twMerge(
                clsx("border-secondary text-secondary btn btn-circle", {
                  invisible: currentStep === 0,
                })
              )}
              onClick={() => handleStepChange(currentStep - 1)}
              title="Previous slide"
            >
              ❮
            </button>

            <button
              className={twMerge(
                clsx("border-secondary text-secondary btn btn-circle next", {
                  invisible: currentStep === 2,
                })
              )}
              onClick={() => handleStepChange(currentStep + 1)}
              title="Next slide"
            >
              ❯
            </button>
          </div>
       
        </div>
        <div className="py-8 flex rounded-lg w-full h-max justify-center gap-8 bg-black bg-opacity-10">
        <iframe title="Rio-UFRJ-Brazil: YOB3 (2024) - Project Promotion [English]" className="rounded-md" width="560" height="315" src="https://video.igem.org/videos/embed/4d92bc3c-a7e1-40dd-aaa7-0cd56bbd184d" ></iframe>
        
        <iframe title="Rio-UFRJ-Brazil: YOB3: An Alternative for the Biotechnological Production of Pla... (2024) - Team Presentation [English]" className="rounded-xl" width="560" height="315" src="https://video.igem.org/videos/embed/25f62533-1f68-4362-b1d3-49d82bac4052" ></iframe>
        </div>
      </div>

      <InfiniteScrollRedirect redirectTo="/team" />
    </section>
  );
};

export default Home;
